export type ExportFieldOption = { key: string; label: string }

/**
 * Account fields read from the account record rather than the search index.
 * Exported only for companies with `Feature:AccountExportAuditFields` enabled.
 */
export const ACCOUNT_RECORD_EXPORT_FIELDS: ExportFieldOption[] = [
  { key: 'lastModifiedBy', label: 'Last Modified By' },
  { key: 'updatedAt', label: 'Updated At' },
  { key: 'lastUpdateProcess', label: 'Last Update Process' },
  { key: 'importId', label: 'Import ID' },
  { key: 'ownerHistory', label: 'Owner History' },
  { key: 'teamHistory', label: 'Team History' },
]

export const ACCOUNT_RECORD_EXPORT_FIELD_KEYS: string[] = ACCOUNT_RECORD_EXPORT_FIELDS.map((field) => field.key)

export const ACCOUNT_EXPORT_FIELDS: ExportFieldOption[] = [
  { key: 'accountId', label: 'Account ID' },
  { key: 'source', label: 'Source' },
  { key: 'sourceId', label: 'Source ID' },
  { key: 'sourceStatus', label: 'Source Status' },
  { key: 'externalLeadId', label: 'External Lead ID' },
  { key: 'status', label: 'Status' },
  { key: 'statusId', label: 'Status ID' },
  { key: 'company.id', label: 'Company ID' },
  { key: 'company.name', label: 'Company Name' },
  { key: 'owner.id', label: 'Owner ID' },
  { key: 'owner.name', label: 'Owner Name' },
  { key: 'closer.id', label: 'Closer ID' },
  { key: 'closer.name', label: 'Closer Name' },
  { key: 'team.id', label: 'Team ID' },
  { key: 'team.name', label: 'Team Name' },
  { key: 'teamId', label: 'Team ID (Legacy)' },
  { key: 'locationId', label: 'Location ID' },
  { key: 'address.address', label: 'Address' },
  { key: 'address.city', label: 'City' },
  { key: 'address.state', label: 'State' },
  { key: 'address.zip', label: 'ZIP Code' },
  { key: 'address.unitNbr', label: 'Unit Number' },
  { key: 'propertyCoordinates.latitude', label: 'Latitude' },
  { key: 'propertyCoordinates.longitude', label: 'Longitude' },
  { key: 'createdDate', label: 'Created Date' },
  { key: 'lastActionDate', label: 'Last Action Date' },
  { key: 'resident.businessName', label: 'Business Name' },
  { key: 'resident.firstName', label: 'First Name' },
  { key: 'resident.lastName', label: 'Last Name' },
  { key: 'resident.email', label: 'Email' },
  { key: 'resident.phone', label: 'Phone' },
  { key: 'resident2.businessName', label: 'Secondary Business Name' },
  { key: 'resident2.firstName', label: 'Secondary First Name' },
  { key: 'resident2.lastName', label: 'Secondary Last Name' },
  { key: 'resident2.email', label: 'Secondary Email' },
  { key: 'resident2.phone', label: 'Secondary Phone' },
  { key: 'notes', label: 'Notes' },
  ...ACCOUNT_RECORD_EXPORT_FIELDS,
  { key: 'workflowHistory', label: 'Status History' },
]

export const ACCOUNT_WORKFLOW_EXPORT_FIELDS: ExportFieldOption[] = [
  { key: 'accountId', label: 'Account ID' },
  { key: 'source', label: 'Source' },
  { key: 'sourceId', label: 'Source ID' },
  { key: 'sourceStatus', label: 'Source Status' },
  { key: 'externalLeadId', label: 'External Lead ID' },
  { key: 'company.id', label: 'Company ID' },
  { key: 'company.name', label: 'Company Name' },
  { key: 'owner.id', label: 'Owner ID' },
  { key: 'owner.name', label: 'Owner Name' },
  { key: 'closer.id', label: 'Closer ID' },
  { key: 'closer.name', label: 'Closer Name' },
  { key: 'team.id', label: 'Team ID' },
  { key: 'team.name', label: 'Team Name' },
  { key: 'teamId', label: 'Team ID (Legacy)' },
  { key: 'locationId', label: 'Location ID' },
  { key: 'address.address', label: 'Address' },
  { key: 'address.city', label: 'City' },
  { key: 'address.state', label: 'State' },
  { key: 'address.zip', label: 'ZIP Code' },
  { key: 'address.unitNbr', label: 'Unit Number' },
  { key: 'propertyCoordinates.latitude', label: 'Latitude' },
  { key: 'propertyCoordinates.longitude', label: 'Longitude' },
  { key: 'createdDate', label: 'Created Date' },
  { key: 'lastActionDate', label: 'Last Action Date' },
  { key: 'resident.firstName', label: 'First Name' },
  { key: 'resident.lastName', label: 'Last Name' },
  { key: 'resident.email', label: 'Email' },
  { key: 'resident.phone', label: 'Phone' },
  { key: 'resident2.firstName', label: 'Secondary First Name' },
  { key: 'resident2.lastName', label: 'Secondary Last Name' },
  { key: 'resident2.email', label: 'Secondary Email' },
  { key: 'resident2.phone', label: 'Secondary Phone' },
  { key: 'notes', label: 'Notes' },
  { key: 'tags', label: 'Tags' },
  { key: 'workflowAction', label: 'Workflow Action' },
  { key: 'workflowActionId', label: 'Workflow Action ID' },
  { key: 'workflowStage', label: 'Workflow Stage' },
  { key: 'workflowStageId', label: 'Workflow Stage ID' },
  { key: 'workflowId', label: 'Workflow ID' },
  ...ACCOUNT_RECORD_EXPORT_FIELDS,
  { key: 'workflowHistory', label: 'Stage History' },
]

export const ACCOUNT_EXPORT_FIELD_KEYS: string[] = ACCOUNT_EXPORT_FIELDS.map((field) => field.key)
export const ACCOUNT_WORKFLOW_EXPORT_FIELD_KEYS: string[] = ACCOUNT_WORKFLOW_EXPORT_FIELDS.map((field) => field.key)

export const ACCOUNT_EXPORT_FIELDS_DESCRIPTION = [
  'Optional list of field keys to include in the export (standard field keys or custom field IDs). ' +
    'Only supported for the Account export type. If omitted, all fields are included.',
  '',
  'Account exports can also include these fields. ' +
    'They are enabled per company on request; contact Terros support to turn them on. ' +
    'For companies without them enabled, requesting one of these keys is accepted but the field is left out of the export.',
  '',
  '- `lastModifiedBy`: user ID of the most recent modifier of the account',
  '- `updatedAt`: when the account was last updated, in UTC milliseconds',
  '- `lastUpdateProcess`: name of the process that last updated the account. ' +
    'This value is informational and is subject to change without notice; do not build logic that depends on specific values.',
  '- `importId`: ID of the import that created the account',
  '- `ownerHistory`: history of account owners, in the same shape as the Account API',
  '- `teamHistory`: history of account teams, in the same shape as the Account API',
  '- `workflowHistory`: when enabled, selecting `workflowHistory` (or omitting `fields`) also includes the ' +
    'workflow stages and actions the account has progressed through, in the same shape as the Account API',
  '',
  'In CSV files these columns come after any status, stage, and custom field columns, so existing column positions do not change. ' +
    'Array values are written as a JSON string in a single cell.',
].join('\n')
